import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle, Users, Award, MapPin, Phone } from 'lucide-react';

export default function AboutPage() {
  useEffect(() => {
    document.title = "Om Toa Byg ApS | Din Tømrer & Entreprenør i Ishøj";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Læs om Toa Byg ApS i Ishøj. Vi leverer solidt tømrer- og entreprenørarbejde på Sjælland med fokus på kvalitet, tillid og ærlig rådgivning.');
  }, []);

  return (
    <div className="bg-slate-50">
      {/* Header Banner */}
      <section className="bg-blue-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">Om Toa Byg ApS</h1>
          <p className="mt-4 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Din pålidelige tømrer- og entreprenørvirksomhed i Ishøj med fokus på kvalitet og tryghed.
          </p>
        </div>
      </section>

      {/* Historie & Værdier */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Venstre: Billede */}
            <div className="relative">
              <div className="aspect-w-4 aspect-h-3 rounded-none overflow-hidden shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/image00016.jpeg"
                  alt="Toa Byg ApS tømrerarbejde"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Højre: Tekst */}
            <div className="space-y-6">
              <h2 className="text-base font-semibold text-blue-600 uppercase tracking-widest">Vores baggrund</h2>
              <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
                Solidt håndværk og ærlig rådgivning siden start
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Toa Byg ApS blev grundlagt med en klar vision: At levere tømrer- og entreprenørarbejde af højeste faglige kvalitet, kombineret med en gennemskuelig og ærlig rådgivning. Vi har base i Ishøj, hvorfra vi dækker hele lokalområdet samt Storkøbenhavn.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vores absolutte spidskompetence er tagløsninger – lige fra mindre reparationer og tagrenoveringer til komplette udskiftninger af tagflader på villaer, rækkehuse og erhvervsejendomme. Men vi er også din alsidige partner, når det gælder klassisk tømrerarbejde og større renoveringsopgaver.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vi tror på, at et godt byggeprojekt bygger på tillid. Derfor starter alle vores opgaver med en grundig dialog, hvor vi lytter til dine ønsker og behov, og rådgiver dig om de bedste materialer og løsninger.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href="tel:+4540942420"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-slate-300 text-sm font-semibold rounded-none text-slate-700 bg-transparent hover:bg-slate-50 transition-colors uppercase tracking-wider"
                >
                  <Phone className="h-4 w-4 text-blue-600" />
                  Ring op
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Værdier Sektion */}
      <section className="py-16 lg:py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-base font-semibold text-blue-600 uppercase tracking-widest">Vores værdier</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
              Hvad kan du forvente af Toa Byg ApS?
            </p>
            <p className="mt-4 text-lg text-slate-600">
              Vi arbejder ud fra et sæt faste principper, der sikrer dig en god oplevelse under hele byggeprocessen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Værdi 1 */}
            <div className="bg-white rounded-none p-8 shadow-sm border border-slate-100 text-center">
              <div className="inline-flex p-4 bg-blue-50 rounded-none text-blue-600 mb-6">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase">Solidt håndværk</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi går aldrig på kompromis med kvaliteten. Vi anvender kun anerkendte materialer og udfører arbejdet efter alle kunstens regler, så du får et holdbart resultat.
              </p>
            </div>

            {/* Værdi 2 */}
            <div className="bg-white rounded-none p-8 shadow-sm border border-slate-100 text-center">
              <div className="inline-flex p-4 bg-blue-50 rounded-none text-blue-600 mb-6">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase">Ærlig rådgivning</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vi rådgiver dig til den bedste løsning for dit hus og dit budget. Du får altid et gennemskueligt tilbud uden skjulte ekstraomkostninger eller overraskelser.
              </p>
            </div>

            {/* Værdi 3 */}
            <div className="bg-white rounded-none p-8 shadow-sm border border-slate-100 text-center">
              <div className="inline-flex p-4 bg-blue-50 rounded-none text-blue-600 mb-6">
                <CheckCircle className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase">Pålidelighed</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                En aftale er en aftale. Vi overholder tidsplaner, rydder pænt op efter os selv hver dag, og kommunikerer løbende med dig under hele forløbet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lokal Forankring */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Venstre: Tekst */}
            <div className="space-y-6">
              <h2 className="text-base font-semibold text-blue-600 uppercase tracking-widest">Lokal partner</h2>
              <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
                Vi kender Ishøj og Storkøbenhavn
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Med base i Ishøj har vi et indgående kendskab til lokalområdet og de gældende lokalplaner og byggeregler. Det gør os i stand til at rådgive dig optimalt om, hvilke løsninger der er tilladte og mest hensigtsmæssige i dit område.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vores lokale forankring betyder også, at vi hurtigt kan rykke ud til besigtigelse, og at vi altid er tæt på, hvis der skulle opstå akutte problemer – f.eks. efter en storm, hvor taget har taget skade.
              </p>
              <div className="flex items-center gap-3 text-slate-700 font-semibold">
                <MapPin className="h-6 w-6 text-blue-600 shrink-0" />
                <span className="uppercase">Baldersbækvej 24, 2635 Ishøj</span>
              </div>
            </div>

            {/* Højre: Billede */}
            <div className="aspect-square rounded-none overflow-hidden shadow-xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/image00022.jpeg"
                alt="Professionelt entreprenørarbejde og totalentreprise på Sjælland af Toa Byg ApS"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Kontakt CTA */}
      <section className="py-16 lg:py-20 bg-blue-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight uppercase">Lad os tage en snak om dit projekt</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
            Uanset om du skal have skiftet tag, bygget nyt eller renoveret det gamle, står vi klar med professionel rådgivning og et uforpligtende tilbud.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-semibold rounded-none text-blue-900 bg-white hover:bg-blue-50 transition-colors shadow-md uppercase tracking-wider"
            >
              Kontakt os i dag
            </Link>
            <a
              href="tel:+4540942420"
              className="inline-flex items-center justify-center px-6 py-3.5 border border-blue-400 text-base font-semibold rounded-none text-white bg-transparent hover:bg-blue-800 transition-colors uppercase tracking-wider"
            >
              Ring +45 40 94 24 20
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
